import type { AxiosRequestConfig, AxiosResponse, Method, ResponseType } from 'axios';

/**
 * Represents a map of HTTP request headers.
 */
export interface RequestHeaders {
  [header: string]: string;
}

// Lazy load breaks the cycle: apiService → httpClient → authService → userApiService → apiService
type GetHttpClient = (typeof import('../httpClient'))['getHttpClient'];
let getHttpClientCached: GetHttpClient | undefined;

async function getHttpClientLazy(): Promise<GetHttpClient> {
  if (getHttpClientCached == null) {
    ({ getHttpClient: getHttpClientCached } = await import('../httpClient'));
  }
  return getHttpClientCached;
}

/**
 * Represents a generic request body for API calls.
 */
export interface RequestBody {
  [key: string]: unknown;
}

/**
 * Standardized error response interface for API errors.
 */
export interface ErrorResponse extends Error {
  statusText: string;
  response?: unknown;
  status: number;
}

/**
 * Options for customizing API requests.
 */
export type RequestOptions = {
  /**
   * Specifies the expected response type (e.g., 'json', 'blob', 'text').
   */
  responseType?: ResponseType;
  /**
   * If true, returns the full AxiosResponse object instead of just the data.
   */
  rawResponse?: boolean;
  /**
   * Optional custom status validation function.
   */
  validateStatus?: AxiosRequestConfig['validateStatus'];
  /**
   * Additional headers to include for this specific request.
   */
  headers?: RequestHeaders;
  /**
   * If true, the request is sent without the Bearer token (e.g. anonymous public share).
   * Used by PublicFileService; other services should not set this.
   */
  skipAuth?: boolean;
  signal?: AbortSignal;
  onDownloadProgress?: (progressEvent: { loaded: number; total?: number }) => void;
  /**
   * Per-request Axios timeout in ms. Use for long synchronous directory calls (LDAP test/import/sync).
   * Omit to keep the instance default.
   */
  timeout?: number;
};

/**
 * Base API service class providing standardized HTTP request methods.
 * Designed for extension by specific service classes.
 */
export class APIService {
  protected async request<T = any>(
    endpoint: string,
    method: string,
    body?: RequestBody | string | FormData | unknown[],
    headers?: RequestHeaders,
    baseUrl: string = import.meta.env.VITE_APP_API_BASE_URL,
    options: RequestOptions = {},
  ): Promise<T> {
    const res = await this.requestRaw<T>(endpoint, method, body, headers, baseUrl, options);
    return res.data as T;
  }

  protected async requestRaw<T = any>(
    endpoint: string,
    method: string,
    body?: RequestBody | string | FormData | unknown[],
    headers?: RequestHeaders,
    baseUrl: string = import.meta.env.VITE_APP_API_BASE_URL,
    options: RequestOptions = {},
  ): Promise<AxiosResponse<T>> {
    const getHttpClient = await getHttpClientLazy();
    const instance = getHttpClient(baseUrl);

    const config: AxiosRequestConfig & { skipAuth?: boolean } = {
      url: endpoint,
      method: method.toLowerCase() as Method,
      data: body,
      headers: { ...(headers || {}), ...(options.headers || {}) },
      responseType: options.responseType,
      skipAuth: options.skipAuth,
      ...(options.validateStatus != null && { validateStatus: options.validateStatus }),
      ...(options.signal != null && { signal: options.signal }),
      ...(options.onDownloadProgress != null && {
        onDownloadProgress: options.onDownloadProgress,
      }),
      ...(options.timeout != null && { timeout: options.timeout }),
    };

    return instance.request<T>(config);
  }
}
